import React from 'react';
import { PageHeader, Tabs, Tab } from 'react-bootstrap';
import OverlayTooltip from './OverlayTooltip';
import './Docs.css';

import SyntaxHighlighter from 'react-syntax-highlighter';
import { foundation } from 'react-syntax-highlighter/dist/styles';


class DocsProduceGraphs extends React.Component {
	render() { 
		return (
			<div className="pageContents">
				<PageHeader>Producing Graphs</PageHeader>
				<p>Splinter can draw <a href="https://en.wikipedia.org/wiki/Communication_diagram" target="_blank">communication diagrams</a> with some help from code instrumentation.
				Your code can be instrumented using the <a href="https://github.com/dimitarz/splinter" target="_blank">splinter-logging-util</a> library. This document will tell you exactly how to do that.</p>
				<center>
				<img id="graph_img" alt="700x477" src="/graphing_op_wh.jpg"/>
				</center>

				<PageHeader><h2>Quick Start</h2></PageHeader>
				<p>First, you need to import the splinter-logging-util library in your project. The library is released in the maven central repository under <code>com.splinter-log:splinter-logging-util</code>.</p>
				<h4>Maven</h4>
				<p>Add the following to your pom.xml:</p>
				<SyntaxHighlighter language='xml' style={foundation}>{
`<dependencies>
  ...
  <dependency>
   <groupId>com.splinter-log</groupId>
   <artifactId>splinter-logging-util</artifactId>
   <version>1.2</version>
  </dependency>
</dependencies>`
				}</SyntaxHighlighter>
				<h4>Gradle</h4>
				<p>Add the following to your build.gradle:</p>
				<SyntaxHighlighter language='javascript' style={foundation}>{
`dependencies {
   ...
   compile 'com.splinter-log:splinter-logging-util:1.2'
}`
				}</SyntaxHighlighter>

				<h4>Examples</h4>
				<Tabs defaultActiveKey={1}>
					<Tab eventKey={1} title="Local Component">
						<h4>Instrumenting one class calling another directly.</h4>
						<p>In this example, we instrument a <code>CoffeeMaker</code> asking a <code>WaterHeater</code> to heat some water. The code below measures the <OverlayTooltip id="processing latency" tooltip="the time it takes the receiver to process the message">processing latency</OverlayTooltip> of heating the water.</p>
						<SyntaxHighlighter showLineNumbers={true}  language='java' style={foundation}>{
`public class CoffeeMaker {
    private final static Logger LOG = Logger.getLogger(CoffeeMaker.class);
    public void brew(double temp) { /* temp == 86.4 */
        LOG.info(SLogCall.log("Coffee Time", "heatWater", "temperature", temp));
        waterHeater.heatWater(temp);
    }
}

public class WaterHeater {
    private final static Logger LOG = Logger.getLogger(WaterHeater.class);
    public void heatWater(double temp) {
    	try {
           /* ... heat that water good ... */
           Thread.sleep(1500);
    	} finally {
           LOG.info(SLogStop.log("Coffee Time", "heatWater"));
        }
    }
}`
						}</SyntaxHighlighter>
						<h4>Produced Graph</h4>
						<center><img width={400} alt="example one" src="/docs/ex1.svg"/></center>
						<h4>Takeaways</h4>
						<ul>
							<li><code>Coffee Time</code> is known as the task name. All nodes and edges tagged with the same task belong to the same graph. In simple applications it can be hardcoded. If <code>WaterHeater</code> was shared by some other component not relating to coffee making, you would want to create a task name on a per request basis or generate the name from the parameters.</li>
							<li><code>heatWater</code> is known as the operation and is the message identifier. An operation has a scope beginning with <code>SLogCall</code> and concluded with <code>SLogStop</code>.</li>
							<li><code>"temperature"</code> and <code>temp</code> are custom key-value parameters that can be attached to any message and are displayed in the Splinter UI.</li>
							<li><code>1500ms</code> is the processing latency computed from the scope of the operation. It is displayed in the node and it is cumulative with other events.</li>
						</ul>
						<br/>
					</Tab>
					<Tab eventKey={2} title="Detached Component">
						<h4>Instrumenting one class calling another indirectly.</h4>
						<p>This example shows one component, the <code>CoffeeMaker</code>, indirectly (via a thread pool) asking a <code>WaterHeater</code> to heat some water. The <code>WaterHeater</code> is considered a detached component because the request to heat the water did not come directly from the <code>CoffeeMaker</code>, but instead went through some message delivery system. Another example of this is a class sending an Android <code>Broadcast</code> to another class. When requests are indirect, we can calculate the <OverlayTooltip id="transport latency" tooltip="the time it takes for a message to be delivered">transport latency</OverlayTooltip> which in this case is the overhead of the <code>ThreadPoolExecutor</code>.</p>
						<SyntaxHighlighter showLineNumbers={true} language='java' style={foundation}>{
`public class CoffeeMaker {
    private final static Logger LOG = Logger.getLogger(CoffeeMaker.class);
    private final Executor threadPool = Executors.newCachedThreadPool();

    public void brew(final double temp) { /* temp == 86.4 */
        LOG.info(SLogCall.log("Coffee Time", "heatWater"));
        threadPool.execute(new Runnable() {
            public void run() {
                waterHeater.heatWater(temp);
            }
        });
        /* .. do some other work, flash some lights .. */
    }
}

public class WaterHeater {
    private final static Logger LOG = Logger.getLogger(WaterHeater.class);
    public void heatWater(double temp) {
    	LOG.info(SLogStart.log("Coffee Time", "heatWater", "temperature", temp));
    	try {
           /* ... heat that water good ... */
           Thread.sleep(1500);
    	} finally {
           LOG.info(SLogStop.log("Coffee Time", "heatWater"));
        }
    }
}`
						}</SyntaxHighlighter>
						<h4>Produced Graph</h4>
						<center><img width={400} alt="example two" src="/docs/ex2.svg"/></center>
						<h4>Takeaways</h4>
						<ul>
							<li>The <code>heatWater</code> scope starts with <code>SLogCall</code> when the <code>Runnable</code> is queued, not when the <code>Runnable</code> executes. This allows us to measure the transport latency (<code>5ms</code>) - in this case the overhead of the threadpool.</li>
							<li><code>SLogStart</code> is a new call added at the beginning of the operation. Effectively, it acknowledges the message has been received.</li>
						</ul>
						<br/>
					</Tab>

					<Tab eventKey={3} title="Remote Service">
						<h4>Instrumenting a class making a network request.</h4>
						<p>In this example, we call a 3rd party remote service from an Android Activity. Sometimes, we may want a single component to pretend to be another component - we do this by using <code>withComponentOverride</code></p>
						<SyntaxHighlighter showLineNumbers={true}  language='java' style={foundation}>{
`public class CoffeeActivity extends Activity {
    @Override
    public void onResume() { 
    	super.onResume();
        Log.d("CoffeeActivity", SLogStart.log("Coffee Gateway", "getRecipes"));
        recipes = getRecipes();
        /* populate UI with recipes */
    }

    public List<Recipe> getRecipes() {
    	/* don't do I/O on the main thread, except in contrived examples */
        GetMethod get = new GetMethod("http://coffee-recipes.com/getRecipes");
        /* Execute method & process the data from the input stream. */
        get.releaseConnection();

        Log.d("CoffeeActivity", new SLogStop("Coffee Gateway", "getRecipes")
                                    .withComponentOverride("coffee-recipes.com")
                                    .toString());
    }
}`
						}</SyntaxHighlighter>
						<h4>Produced Graph</h4>
						<center><img width={400} alt="example three"  src="/docs/ex3.svg"/></center>
						<h4>Takeaways</h4>
						<ul>
							<li>The key here is that we produce 2 logs from the same component (<code>CoffeeActivity</code>) but thanks to <code>withComponentOverride()</code>, we can specify <code>coffee-recipes.com</code> as a new component.</li>
						</ul>
						<br/>
					</Tab>

					<Tab eventKey={4} title="Message Broadcast">
						<h4>Instrumenting a broadcast and its receivers.</h4>
						<p>Broadcasts have a single origin and multiple destinations. In the following example, our coffee maker signals that the coffee is ready which results in multiple actions. Android's <code>Broadcast</code>s and Qt's <code>signal</code>s fit this model perfectly.</p>
						<SyntaxHighlighter showLineNumbers={true} language='java' style={foundation}>{
`public class CoffeeMaker {
    public void brew() {
        /* .. prepare brew .. */

        Intent coffeeReadyIntent = new Intent("coffee_ready");
        Log.d("CoffeeMaker", SLogBroadcastSend.log(
                                            "Coffee Time",
                                            coffeeReadyIntent.getAction()));
        sendBroadcast(coffeeReadyIntent);
    }
}

public class WaterTank extends BroadcastReceiver {
    @Override
    public void onReceive(Context context, Intent intent) {
        if ("coffee_ready".equals(intent.getAction())) { 
            Log.d("WaterTank", SLogBroadcastStart.log(
                                                "Coffee Time", 
                                                intent.getAction(),
                                                "ensureWaterCapacity"));
            /* make sure we have enough water for next cup! */
            ensureWaterCapacity();

            Log.d("WaterTank", SLogBroadcastStop.log(
                                                "Coffee Time", 
                                                intent.getAction(),
                                                "ensureWaterCapacity"));
        }
    }

    public void ensureWaterCapacity() {
        /* ... */
    }
}

public class ControlPanel extends BroadcastReceiver {
    @Override
    public void onReceive(Context context, Intent intent) {
        if ("coffee_ready".equals(intent.getAction())) { 
            Log.d("ControlPanel", SLogBroadcastStart.log(
                                                "Coffee Time",
                                                intent.getAction(),
                                                "chime"));
            /* make some noise! */
            chime();

            Log.d("ControlPanel", SLogBroadcastStop.log(
                                                "Coffee Time",
                                                intent.getAction(),
                                                "chime"));
        }
    }

    public void chime() {
        /* ... */
    }
}

`				}</SyntaxHighlighter>
						<h4>Produced Graph</h4>
						<center><img width={400} alt="example four" src="/docs/ex4.svg"/></center>
						<h4>Takeaways</h4>
						<ul>
							<li>A single broadcast is sent by <code>CoffeeMaker</code> and is received by <code>WaterTank</code> and <code>ControlPanel</code>.</li>
							<li>A broadcast id (<code>coffee_time</code>) is what connects the sender and the receivers.</li> 
							<li>The edge latencies are computed based on the <code>SLogBroadcastStart</code> and <code>SLogBroadcastStop</code> statements. Both <code>SLogStart</code>, and <code>SLogBroadcastStart</code> are optional.</li>
						</ul>
					</Tab>
				</Tabs>

				<PageHeader><h2>Appendix</h2></PageHeader>
				<h3>Motivation For Building Diagrams</h3>
				<p>Communication diagrams help explain the dynamic behavior of a system. They are especially useful when: </p>
				<ol>
					<li>Demonstrating how a system functions <i>over time</i>.</li>
					<li>Debugging faulty systems.</li>
				</ol>

				<p>These and other UML diagrams are great for documenting purposes. However, diagram creation is almost always decoupled from the actual running code. This means that as soon as the diagram is created it is also obsolete until the next time someone updates it.</p>
				<p>Splinter solves this problem by providing an <OverlayTooltip id="maven artifact id" tooltip="maven artifact com.splinter-log:splinter-logging-util">instrumentation library</OverlayTooltip> which prints our custom logs statements that are then used to construct the graph.</p>
				
				<h3>Guidelines</h3>
				<p>Communication diagrams represent messaging between components. An application must definine what a component represents. Here are some guidelines on components:</p>
				<ul>
					<li>Components are not typically meant to be functions alone. While you can instrument every function in your code, that will hamper your code's readability, add a performance impact and produce a massive diagram that won't help anyone.</li>
					<li>A component should ideally be independent subsystem. For example, in the <code>CoffeeMaker</code> example above, components are <code>WaterHeater</code> and <code>ControlPanel</code>.</li>
					<li>In <OverlayTooltip id="service oriented architecture" tooltip="Service Oriented Architecture">SOA</OverlayTooltip> applications, the components can be the different (micro) services.</li>
					<li>Android applications define components very well - <code>Service</code>s, <code>BroadcastReceiver</code>s, <code>ContentProvider</code>s, etc.</li>
					<li>In <OverlayTooltip id="inversion of control" tooltip="Such as Spring, Dagger, Guice, etc.">IoC-enabled</OverlayTooltip> applications, the injected <code>Bean</code>s.</li>
					<li>As a general rule of thumb, a class that performs core functionality is considered a component.</li>
					<li>The component field is mapped in Splinter's log definition - for example, android's "tag" is considered a component, but you can override the default mapping in code.</li>
				</ul>
				<br/>
			
			</div>
			);
	}
}

export default DocsProduceGraphs;


// WEBPACK FOOTER //
// ./src/DocsProduceGraphs.js